import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { invoiceTemplatesApi } from '../../api/invoiceTemplates';
import LoadingSpinner from '../../components/LoadingSpinner';

const ACCENT_SWATCHES = ['#071E3B', '#1D4ED8', '#047857', '#B91C1C', '#7C3AED', '#D97706'];
const TITLE_GREY = '#374151';

/**
 * WYSIWYG template canvas: the WHOLE sample invoice is the editing surface.
 * Click the logo slot to upload, the title/grand-total for the accent color,
 * the Mat. vnt. header to toggle the column, the payment block to emphasize,
 * the footer line to type. Every click autosaves; "Atidaryti PDF" opens the
 * authoritative QuestPDF render. The canvas mirrors the PDF structure but need
 * not be pixel-identical - the PDF is the truth.
 */
export default function InvoiceTemplateBuilder() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const savedTimer = useRef<number | null>(null);

  const { data: template, isLoading } = useQuery({
    queryKey: ['invoice-template'],
    queryFn: invoiceTemplatesApi.get,
  });

  const [accent, setAccent] = useState<string | null>(null);
  const [preset, setPreset] = useState('classic');
  const [showUnitColumn, setShowUnitColumn] = useState(true);
  const [emphasizePaymentDetails, setEmphasizePaymentDetails] = useState(true);
  const [footerText, setFooterText] = useState('');
  const [hasLogo, setHasLogo] = useState(false);
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [footerEditing, setFooterEditing] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useMemo(() => {
    if (template && !initialized) {
      setAccent(template.accentColor);
      setPreset(template.preset ?? 'classic');
      setShowUnitColumn(template.showUnitColumn);
      setEmphasizePaymentDetails(template.emphasizePaymentDetails);
      setFooterText(template.footerText ?? '');
      setHasLogo(template.hasLogo);
      setInitialized(true);
    }
  }, [template, initialized]);

  useEffect(() => {
    if (!hasLogo) return;
    let url: string | null = null;
    invoiceTemplatesApi.logoUrl().then(u => { url = u; setLogoSrc(u); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [hasLogo]);

  const flashSaved = () => {
    setSavedFlash(true);
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
  };

  const saveMutation = useMutation({
    mutationFn: (payload: { accent: string | null; preset: string; showUnit: boolean; emphasize: boolean; footer: string }) =>
      invoiceTemplatesApi.save({
        accentColor: payload.accent,
        preset: payload.preset,
        showUnitColumn: payload.showUnit,
        emphasizePaymentDetails: payload.emphasize,
        footerText: payload.footer,
      }),
    onSuccess: () => { flashSaved(); void queryClient.invalidateQueries({ queryKey: ['invoice-template'] }); },
  });

  // Click IS the save: every canvas interaction persists immediately.
  const apply = (patch: Partial<{ accent: string | null; preset: string; showUnit: boolean; emphasize: boolean; footer: string }>) => {
    const next = {
      accent: patch.accent !== undefined ? patch.accent : accent,
      preset: patch.preset !== undefined ? patch.preset : preset,
      showUnit: patch.showUnit !== undefined ? patch.showUnit : showUnitColumn,
      emphasize: patch.emphasize !== undefined ? patch.emphasize : emphasizePaymentDetails,
      footer: patch.footer !== undefined ? patch.footer : footerText,
    };
    if (patch.accent !== undefined) setAccent(patch.accent);
    if (patch.preset !== undefined) setPreset(patch.preset);
    if (patch.showUnit !== undefined) setShowUnitColumn(patch.showUnit);
    if (patch.emphasize !== undefined) setEmphasizePaymentDetails(patch.emphasize);
    if (patch.footer !== undefined) setFooterText(patch.footer);
    saveMutation.mutate(next);
  };

  const uploadMutation = useMutation({
    mutationFn: (file: File) => invoiceTemplatesApi.uploadLogo(file),
    onSuccess: async () => {
      setHasLogo(true);
      flashSaved();
      await queryClient.invalidateQueries({ queryKey: ['invoice-template'] });
    },
  });

  const removeLogoMutation = useMutation({
    mutationFn: invoiceTemplatesApi.removeLogo,
    onSuccess: async () => {
      setHasLogo(false);
      setLogoSrc(null);
      flashSaved();
      await queryClient.invalidateQueries({ queryKey: ['invoice-template'] });
    },
  });

  const handleLogoPick = (files: FileList | null) => {
    const file = files?.[0];
    if (file) {
      const localUrl = URL.createObjectURL(file);
      setLogoSrc(localUrl);
      uploadMutation.mutate(file);
    }
  };

  const handlePreview = async () => {
    const tab = window.open('', '_blank');
    try {
      const url = await invoiceTemplatesApi.previewUrl();
      if (tab) tab.location.href = url;
    } catch (err) {
      tab?.close();
    }
  };

  const commitFooter = () => {
    setFooterEditing(false);
    apply({ footer: footerText });
  };

  if (isLoading) return <LoadingSpinner />;

  const accentColor = accent ?? TITLE_GREY;

  const sampleLines = [
    { name: 'Konsultacinės paslaugos', unit: 'val.', qty: '10,00', price: '20,00', vat: '21%', sum: '200,00', sumVat: '42,00' },
    { name: 'Prekės pavadinimas', unit: 'vnt.', qty: '2,00', price: '50,00', vat: '21%', sum: '100,00', sumVat: '21,00' },
  ];

  return (
    <div className="max-w-5xl space-y-3">
      <div className="flex flex-wrap items-center gap-3 py-2 border-b border-gray-100">
        <h1 className="text-xl font-semibold text-gray-900">{t('invoiceTemplate.title')}</h1>
        <span className="text-xs text-gray-400">{t('invoiceTemplate.canvasHint')}</span>
        <div className="ml-auto flex items-center gap-3">
          {savedFlash && <span className="text-xs text-green-600">{t('invoiceTemplate.saved')}</span>}
          <button
            type="button"
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            onClick={handlePreview}
          >
            {t('invoiceTemplate.preview')}
          </button>
        </div>
      </div>

      {/* Preset chooser */}
      <div className="flex items-center gap-2">
        {(['classic', 'minimal', 'modern'] as const).map(p => (
          <button
            key={p}
            type="button"
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              preset === p
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
            }`}
            onClick={() => apply({ preset: p })}
          >
            {t(`invoiceTemplate.preset_${p}`)}
          </button>
        ))}
      </div>

      {/* The invoice itself — a scaled A4 sheet on a grey desk */}
      <div className="bg-gray-200/70 rounded-xl p-4 md:p-8 overflow-x-auto">
        <div
          className="bg-white shadow-xl rounded-sm mx-auto relative flex flex-col"
          style={{
            width: 620,
            minWidth: 620,
            minHeight: 877,
            padding: '44px 48px',
            // Same family chain as the QuestPDF page default - canvas and PDF
            // render with matching typography.
            fontFamily: "'Lato', 'Segoe UI', system-ui, sans-serif",
          }}
        >
          {preset === 'modern' && (
            <div className="absolute top-0 left-0 right-0" style={{ height: 6, backgroundColor: accentColor }} />
          )}
        {/* Header: title + color popover | logo slot + meta.
            No separator line - the PDF draws none either (canvas mirrors the PDF). */}
        <div className="flex justify-between items-start pb-4 mb-5 relative">
          <div className="relative">
            <h2
              className={`${preset === 'minimal' ? 'text-lg font-medium' : 'text-2xl font-bold'} cursor-pointer hover:opacity-80`}
              style={{ color: accentColor }}
              onClick={() => setColorOpen(o => !o)}
              title={t('invoiceTemplate.accentColor')}
            >
              SĄSKAITA FAKTŪRA
            </h2>
            {colorOpen && (
              <div className="absolute top-full left-0 mt-2 z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-56">
                <div className="grid grid-cols-6 gap-2 mb-2">
                  {ACCENT_SWATCHES.map(sw => (
                    <button
                      key={sw}
                      type="button"
                      className="h-7 w-7 rounded border border-gray-200 hover:scale-110 transition-transform"
                      style={{ backgroundColor: sw }}
                      onClick={() => { apply({ accent: sw }); setColorOpen(false); }}
                      title={sw}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                  onClick={() => { apply({ accent: null }); setColorOpen(false); }}
                >
                  {t('invoiceTemplate.accentNone')}
                </button>
              </div>
            )}
          </div>

          <div className="text-right space-y-2">
            {/* Logo slot: click to add, hover ✕ to remove */}
            <div className="group relative inline-block">
              {hasLogo && logoSrc ? (
                <img src={logoSrc} alt="logo" className="h-12 inline-block" />
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="h-14 w-40 border-2 border-dashed border-gray-300 rounded text-xs text-gray-400 hover:border-primary-400 hover:text-primary-500"
                  title={t('invoiceTemplate.logoUpload')}
                >
                  {uploadMutation.isPending ? t('invoiceTemplate.uploading') : t('invoiceTemplate.logoUpload')}
                </button>
              )}
              {hasLogo && (
                <button
                  type="button"
                  className="hidden group-hover:block absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white text-xs leading-none"
                  onClick={() => removeLogoMutation.mutate()}
                  title={t('invoiceTemplate.logoRemove')}
                >
                  ✕
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={e => { handleLogoPick(e.target.files); e.target.value = ''; }}
              />
            </div>
            <div className="text-sm">
              <div className="font-semibold">SF Nr.: SF000001</div>
              <div className="text-gray-500">Data: {new Date().toISOString().slice(0, 10)}</div>
              <div className="text-gray-500">Valiuta: EUR</div>
            </div>
          </div>
        </div>

        {/* Seller / buyer */}
        <div className="grid grid-cols-2 gap-6 mb-5">
          <div className="text-sm">
            <div className="text-xs text-gray-400 mb-1">PARDUOTĖJAS</div>
            <div className="font-semibold">UAB Pavyzdys</div>
            <div className="text-gray-500">Įmonės kodas: 123456789</div>
            <div className="text-gray-500">Gatvė 1, Vilnius</div>
            <div
              className={`mt-2 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 ${emphasizePaymentDetails ? 'font-semibold text-[13px]' : 'text-gray-500'}`}
              onClick={() => apply({ emphasize: !emphasizePaymentDetails })}
              title={t('invoiceTemplate.emphasizePayment')}
            >
              Apmokėjimo rekvizitai: LT00 0000 0000 0000
            </div>
          </div>
          <div className="text-sm">
            <div className="text-xs text-gray-400 mb-1">PIRKĖJAS</div>
            <div className="font-semibold">UAB Klientas</div>
            <div className="text-gray-500">Įmonės kodas: 987654321</div>
            <div className="text-gray-500">Prospectas 2, Kaunas</div>
          </div>
        </div>

        <div className="text-sm text-gray-600 mb-4">Pavyzdinė sąskaita šablono peržiūrai</div>

        {/* Lines: the Mat. vnt. header is the toggle. Preset styles the header row. */}
        <table className="w-full text-sm">
          <thead>
            <tr className={
              preset === 'minimal'
                ? 'text-left border-b-2 border-gray-500'
                : preset === 'modern'
                  ? 'text-left'
                  : 'bg-gray-100 text-left'
            }
            style={preset === 'modern' ? { backgroundColor: accentColor, color: '#FFFFFF' } : undefined}>
              <th className="px-2 py-2">Eil. Nr.</th>
              <th className="px-2 py-2">Pavadinimas / aprašymas</th>
              {showUnitColumn && (
                <th
                  className="px-2 py-2 cursor-pointer hover:text-primary-600"
                  onClick={() => apply({ showUnit: false })}
                  title={t('invoiceTemplate.hideUnit')}
                >
                  Mat. vnt. ⨯
                </th>
              )}
              <th className="px-2 py-2 text-right">Kiekis</th>
              <th className="px-2 py-2 text-right">Kaina</th>
              <th className="px-2 py-2 text-right">PVM %</th>
              <th className="px-2 py-2 text-right">Suma</th>
              <th className="px-2 py-2 text-right">PVM suma</th>
            </tr>
          </thead>
          <tbody>
            {sampleLines.map((l, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td className="px-2 py-2 text-right">{i + 1}</td>
                <td className="px-2 py-2">{l.name}</td>
                {showUnitColumn && <td className="px-2 py-2">{l.unit}</td>}
                <td className="px-2 py-2 text-right">{l.qty}</td>
                <td className="px-2 py-2 text-right">{l.price}</td>
                <td className="px-2 py-2 text-right">{l.vat}</td>
                <td className="px-2 py-2 text-right">{l.sum}</td>
                <td className="px-2 py-2 text-right">{l.sumVat}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!showUnitColumn && (
          <button
            type="button"
            className="mt-1 text-xs text-gray-400 hover:text-primary-600 underline"
            onClick={() => apply({ showUnit: true })}
          >
            + {t('invoiceTemplate.showUnit')}
          </button>
        )}

        {/* Totals: grand total opens the same accent popover */}
        <div className="flex justify-end mt-4">
          <div className="text-sm space-y-1 text-right">
            <div className="text-gray-500">Iš viso be PVM: 300,00 EUR</div>
            <div className="text-gray-500">PVM iš viso: 63,00 EUR</div>
            <div
              className="text-lg font-bold cursor-pointer hover:opacity-80"
              style={{ color: accentColor }}
              onClick={() => setColorOpen(o => !o)}
              title={t('invoiceTemplate.accentColor')}
            >
              Iš viso mokėti: 363,00 EUR
            </div>
          </div>
        </div>

        <div className="text-sm text-gray-600 mt-4">Apmokėti iki {new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10)}</div>

        {/* Footer: click to edit inline. No separator line (PDF has none). */}
        <div className="mt-6 text-xs">
          {footerEditing ? (
            <input
              autoFocus
              type="text"
              className="w-full border border-primary-300 rounded px-2 py-1 text-xs"
              value={footerText}
              onChange={e => setFooterText(e.target.value)}
              onBlur={commitFooter}
              onKeyDown={e => { if (e.key === 'Enter') commitFooter(); }}
              placeholder={t('invoiceTemplate.footerPlaceholder')}
            />
          ) : (
            <div
              className="cursor-pointer text-gray-400 hover:text-gray-600"
              onClick={() => setFooterEditing(true)}
              title={t('invoiceTemplate.footerText')}
            >
              {footerText || t('invoiceTemplate.footerAdd')}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
